import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { type ReactNode, useEffect } from "react";

/**
 * The side panel beside the page (F-206, F-207): 420px, full height below
 * any banner, the page narrows instead of being covered. One panel at a
 * time; ✕, Esc or the panel's own top bar button close it.
 */
export function ShellSidePanel({
  children,
  headerActions,
  id,
  onClose,
  open = true,
  subtitle,
  title,
  ...rest
}: {
  children: ReactNode;
  headerActions?: ReactNode;
  id: string;
  onClose: () => void;
  open?: boolean;
  subtitle?: string;
  title: string;
  /** Marks the Log issue panel so its element picker ignores the panel. */
  "data-hc-admin-issue-widget-root"?: boolean;
}) {
  // F-215: Esc closes the top-most thing. A dialog or picker inside the
  // panel that handles Esc itself marks the event handled first.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        event.preventDefault();
        onClose();
      }
    };
    // Window bubble runs after the focused control, React dialogs and the
    // shell's document-level popovers, including when focus is on the page.
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  return (
    <aside
      aria-label={title}
      className="vnext-shell-panel"
      hidden={!open}
      id={open ? id : undefined}
      style={open ? undefined : { display: "none" }}
      {...rest}
    >
      <div className="vnext-shell-panel__header">
        <div className="vnext-shell-panel__heading">
          <h2 className="vnext-shell-panel__title">{title}</h2>
          {subtitle ? (
            <p className="vnext-shell-panel__subtitle">{subtitle}</p>
          ) : null}
        </div>
        {headerActions}
        <button
          aria-label={`Close ${title}`}
          className="vnext-shell-panel__close"
          onClick={onClose}
          type="button"
        >
          <CloseOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
        </button>
      </div>
      <div className="vnext-shell-panel__body">{children}</div>
    </aside>
  );
}
